import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CalendarPlus, Banknote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { AdminShell } from '../components/AdminShell';
import { api, errMsg } from '../lib/api';
import { peso, manilaDate, manilaDateTime, PLAN_LABELS, METHOD_LABELS } from '../lib/format';

export default function AdminShopDetailPage() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState('');
  const [extendDays, setExtendDays] = useState('30');
  const [confirmAction, setConfirmAction] = useState(null); // suspend | cancel | reactivate
  const [pay, setPay] = useState({ amount: '', method: 'gcash', reference: '', period_start: '', period_end: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/shops/${shopId}`);
      setData(d);
      setPlan(d.shop.plan);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [shopId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const changePlan = () => run(async () => {
    const { data: d } = await api.post(`/admin/shops/${shopId}/plan`, { plan });
    toast.success(d.message);
  });

  const extendTrial = () => run(async () => {
    const { data: d } = await api.post(`/admin/shops/${shopId}/extend-trial`, { days: parseInt(extendDays, 10) });
    toast.success(d.message);
  });

  const changeStatus = (action) => run(async () => {
    const { data: d } = await api.post(`/admin/shops/${shopId}/status`, { action });
    toast.success(d.message);
    setConfirmAction(null);
  });

  const recordPayment = (e) => {
    e.preventDefault();
    run(async () => {
      const { data: d } = await api.post(`/admin/shops/${shopId}/subscription-payments`, {
        amount_centavos: Math.round(parseFloat(pay.amount) * 100),
        method: pay.method,
        reference: pay.reference || null,
        period_start: new Date(pay.period_start).toISOString(),
        period_end: new Date(pay.period_end).toISOString(),
        notes: pay.notes || null,
      });
      toast.success(d.message);
      setPay({ amount: '', method: 'gcash', reference: '', period_start: '', period_end: '', notes: '' });
    });
  };

  if (!data) {
    return <AdminShell><Skeleton className="h-64 rounded-2xl" /></AdminShell>;
  }

  const s = data.shop;

  return (
    <AdminShell title="Shop detail">
      <Button data-testid="admin-back-button" variant="ghost" className="rounded-xl -ml-2" onClick={() => navigate('/admin')}>
        <ArrowLeft className="h-4 w-4 mr-1.5" /> All shops
      </Button>

      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-1.5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h1 data-testid="admin-shop-name" className="text-lg font-semibold font-display">{s.name}</h1>
            <span className="text-sm capitalize font-medium">{PLAN_LABELS[s.plan]} · {s.subscription_status.replace('_', ' ')}</span>
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {s.user_count} users · {s.open_job_count} open jobs · created {manilaDate(s.created_at)}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Trial ends {manilaDate(s.trial_ends_at) || '—'} · Paid until {manilaDate(s.paid_until) || '—'}
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Plan + trial + status */}
        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5 space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Change plan</p>
              <div className="flex gap-2">
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger data-testid="admin-plan-select" className="h-11 rounded-xl flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="admin-change-plan-button" disabled={busy || plan === s.plan} onClick={changePlan} className="h-11 rounded-xl">Apply</Button>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Extend trial</p>
              <div className="flex gap-2">
                <Input data-testid="admin-extend-days-input" type="number" min="1" max="365" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} className="h-11 rounded-xl w-24" />
                <Button data-testid="admin-extend-trial-button" variant="outline" disabled={busy} onClick={extendTrial} className="h-11 rounded-xl flex-1">
                  <CalendarPlus className="h-4 w-4 mr-1.5" /> Extend by days
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Account status</p>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="admin-suspend-button" variant="outline" disabled={busy} className="h-10 rounded-xl text-amber-700 border-amber-300" onClick={() => setConfirmAction('suspend')}>Suspend</Button>
                <Button data-testid="admin-reactivate-button" variant="outline" disabled={busy} className="h-10 rounded-xl text-emerald-700 border-emerald-300" onClick={() => setConfirmAction('reactivate')}>Reactivate</Button>
                <Button data-testid="admin-cancel-button" variant="outline" disabled={busy} className="h-10 rounded-xl text-red-700 border-red-300" onClick={() => setConfirmAction('cancel')}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Record subscription payment */}
        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5">
            <p className="text-sm font-medium mb-3 flex items-center gap-1.5"><Banknote className="h-4 w-4" /> Record subscription payment</p>
            <form onSubmit={recordPayment} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[hsl(var(--muted-foreground))]">₱</span>
                  <Input data-testid="admin-payment-amount-input" type="number" step="0.01" min="0.01" required placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="h-11 rounded-xl pl-7" />
                </div>
                <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                  <SelectTrigger data-testid="admin-payment-method-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="gcash">GCash</SelectItem>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input data-testid="admin-payment-reference-input" placeholder="Reference (optional)" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="h-11 rounded-xl" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">Period from</label>
                  <Input data-testid="admin-payment-start-input" type="date" required value={pay.period_start} onChange={(e) => setPay({ ...pay, period_start: e.target.value })} className="h-11 rounded-xl" />
                </div>
                <div>
                  <label className="text-xs text-[hsl(var(--muted-foreground))]">Period to</label>
                  <Input data-testid="admin-payment-end-input" type="date" required value={pay.period_end} onChange={(e) => setPay({ ...pay, period_end: e.target.value })} className="h-11 rounded-xl" />
                </div>
              </div>
              <Input data-testid="admin-payment-notes-input" placeholder="Notes (optional)" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} className="h-11 rounded-xl" />
              <Button data-testid="admin-record-payment-button" type="submit" disabled={busy} className="w-full h-11 rounded-xl">
                {busy ? 'Recording…' : 'Record payment → activate'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Payment history */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-2">
          <h2 className="text-base font-semibold font-display">Subscription payment history</h2>
          {data.subscription_payments.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No payments recorded yet.</p>
          ) : (
            data.subscription_payments.map((p) => (
              <div key={p.id} data-testid="admin-payment-history-row" className="flex items-center justify-between text-sm border-b border-[hsl(var(--border))] last:border-0 pb-2 last:pb-0">
                <div>
                  <span className="font-medium">{METHOD_LABELS[p.method] || p.method}</span>
                  {p.reference && <span className="text-[hsl(var(--muted-foreground))]"> · {p.reference}</span>}
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {manilaDate(p.period_start)} → {manilaDate(p.period_end)} · recorded {manilaDateTime(p.recorded_at)}
                  </div>
                </div>
                <span className="font-semibold tabular-nums">{peso(p.amount_centavos)}</span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Users (metadata only) */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-2">
          <h2 className="text-base font-semibold font-display">Users</h2>
          {data.users.map((u) => (
            <div key={u.id} className="text-sm flex items-center justify-between">
              <span>{u.name} <span className="text-[hsl(var(--muted-foreground))]">· {u.email}</span></span>
              <span className="text-xs capitalize text-[hsl(var(--muted-foreground))]">{u.role}{!u.active ? ' (inactive)' : ''}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Admin events */}
      <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
        <CardContent className="pt-5 space-y-2">
          <h2 className="text-base font-semibold font-display">Admin activity</h2>
          {data.admin_events.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No admin actions yet.</p>
          ) : (
            data.admin_events.map((ev) => (
              <div key={ev.id} data-testid="admin-event-row" className="text-xs flex gap-2">
                <span className="text-[hsl(var(--muted-foreground))] shrink-0 w-36">{manilaDateTime(ev.created_at)}</span>
                <span>{ev.action.replaceAll('_', ' ')}</span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(o) => !o && setConfirmAction(null)}
        title={`${confirmAction === 'suspend' ? 'Suspend' : confirmAction === 'cancel' ? 'Cancel' : 'Reactivate'} ${s.name}?`}
        consequence={
          confirmAction === 'reactivate'
            ? 'The shop regains write access based on its paid/trial period.'
            : 'The shop becomes read-only. Nothing is deleted and public customer pages keep working.'
        }
        confirmLabel={confirmAction === 'suspend' ? 'Suspend shop' : confirmAction === 'cancel' ? 'Cancel subscription' : 'Reactivate shop'}
        destructive={confirmAction !== 'reactivate'}
        busy={busy}
        onConfirm={() => changeStatus(confirmAction)}
      />
    </AdminShell>
  );
}
